import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import SectionHead from "./SectionHead";
import { waLink } from "@/lib/site";

const PICKUPS = ["Rayagada", "Bhubaneswar", "Visakhapatnam", "Berhampur"];

const CIRCUITS = {
  "Puri – Konark Heritage": 800,
  "Chilika – Gopalpur Coast": 620,
  "Koraput – Tribal Valleys": 420,
  "Daringbadi Hills": 560,
  "Araku Valley & Borra Caves": 460,
  "Custom Route (approx.)": 700,
};

const CARS = {
  "Budget Hatchback": 10,
  "Sedan (Dzire / Ciaz)": 12,
  "Innova Crysta": 18,
  "Toyota Fortuner": 22,
  "Tempo Traveller": 24,
};

const DRIVER_PER_DAY = 400;

export default function Estimator() {
  const [pickup, setPickup] = useState(PICKUPS[0]);
  const [circuit, setCircuit] = useState(Object.keys(CIRCUITS)[0]);
  const [car, setCar] = useState("Innova Crysta");
  const [days, setDays] = useState(3);

  const estimate = useMemo(() => {
    const base = CIRCUITS[circuit] * CARS[car] + days * DRIVER_PER_DAY;
    return { low: Math.round(base * 0.92), high: Math.round(base * 1.08) };
  }, [circuit, car, days]);

  const fmt = (n) => `₹${n.toLocaleString("en-IN")}`;

  const message = `Hello Jay Jagannath Tour & Travels, I need an exact quote.\nPickup: ${pickup}\nTrip: ${circuit}\nVehicle: ${car}\nDuration: ${days} day(s)\nYour website estimate: ${fmt(estimate.low)} – ${fmt(estimate.high)}`;

  const selectCls =
    "w-full rounded-xl border border-white/15 bg-[#0F1418] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#D4AF37]";

  return (
    <section id="estimator" data-testid="trip-estimator-section" className="bg-[#0B1014] py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHead
          overline="Instant estimate"
          title="Price your journey in seconds"
          sub="Pick a circuit, a car and the days — get an indicative fare and send it straight to us for the exact quote."
        />
        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-5 rounded-2xl border border-white/10 bg-[#171F26] p-8 lg:col-span-3"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="estimator-pickup" className="mb-2 block text-xs uppercase tracking-widest text-white/50">Pickup city</label>
                <select id="estimator-pickup" data-testid="estimator-pickup-select" value={pickup} onChange={(e) => setPickup(e.target.value)} className={selectCls}>
                  {PICKUPS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="estimator-destination" className="mb-2 block text-xs uppercase tracking-widest text-white/50">Destination circuit</label>
                <select id="estimator-destination" data-testid="estimator-destination-select" value={circuit} onChange={(e) => setCircuit(e.target.value)} className={selectCls}>
                  {Object.keys(CIRCUITS).map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="estimator-car" className="mb-2 block text-xs uppercase tracking-widest text-white/50">Vehicle</label>
              <select id="estimator-car" data-testid="estimator-car-select" value={car} onChange={(e) => setCar(e.target.value)} className={selectCls}>
                {Object.keys(CARS).map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="estimator-days" className="mb-2 flex justify-between text-xs uppercase tracking-widest text-white/50">
                <span>Duration</span>
                <span className="font-semibold text-[#D4AF37]" data-testid="estimator-days-value">{days} day{days > 1 ? "s" : ""}</span>
              </label>
              <input
                id="estimator-days"
                data-testid="estimator-days-slider"
                type="range"
                min="1"
                max="10"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="w-full accent-[#D4AF37]"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col justify-between rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-b from-[#171F26] to-[#12181d] p-8 lg:col-span-2"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Indicative fare</p>
              <p data-testid="estimator-result" className="mt-4 font-serif text-4xl font-bold">
                {fmt(estimate.low)} <span className="text-white/40">–</span> {fmt(estimate.high)}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-white/55">
                Covers vehicle, fuel and driver allowance for roughly {CIRCUITS[circuit]} km over {days} day{days > 1 ? "s" : ""}. Tolls, parking and stays extra.
              </p>
            </div>
            <a
              href={waLink(message)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="estimator-send-whatsapp"
              className="mt-8 flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-[#0F1418] transition-transform hover:scale-[1.03]"
            >
              <MessageCircle className="h-4 w-4" /> Get Exact Quote on WhatsApp
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
